import { Box, CircularProgress, ThemeProvider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import AccountTabs from 'components/v2/accountTabs';
import PageHeader from 'components/v2/common/pageHeader';
import Result from 'components/v2/common/Result';
import PageBackground from 'components/v2/PageBackground';
import { MyLicensesForm } from 'views/v2/licenses/components/myLicensesForm';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { ConsultantLicense, MyLicensesFormValues } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import {
    getConsultantLicenseEdits,
    persistConsultantLicenseEdits,
} from 'views/v2/adminPanel/helpers/adminConsultants';

type LoadStatus = 'loading' | 'ready' | 'error';

export default function MyLicenses() {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [licenses, setLicenses] = useState<ConsultantLicense[] | null>(null);
    const [status, setStatus] = useState<LoadStatus>('loading');

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setStatus('loading');
            try {
                const response = await api.me.listLicenses();
                if (cancelled) return;
                setLicenses(response.data);
                setStatus('ready');
            } catch (error) {
                if (cancelled) return;
                setLicenses(null);
                setStatus('error');
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('failedToLoadLicenses')),
                        type: 'error',
                    })
                );
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, translate]);

    const handleSubmit = async (values: MyLicensesFormValues) => {
        if (!licenses) return;

        try {
            const savedLicenses = await persistConsultantLicenseEdits(
                'currentUser',
                licenses,
                getConsultantLicenseEdits({ licenses }, values),
                values.licenses
            );
            setLicenses(savedLicenses);
            dispatch(setAlert({ message: translate('licensesUpdated'), type: 'success' }));
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('failedToUpdateLicenses')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground maxWidth="sm">
                <AccountTabs />
                <PageHeader size="small" title="regulatedImmigrationPractitionerLicences" />

                {status === 'loading' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : status === 'ready' && licenses ? (
                    <MyLicensesForm licenses={licenses} onSubmit={handleSubmit} />
                ) : (
                    <Result
                        title={translate('failedToLoadLicenses')}
                        description={translate('myLicensesUnavailableDescription')}
                    />
                )}
            </PageBackground>
        </ThemeProvider>
    );
}
